import { createStore, type StoreApi } from 'zustand';

import type { CitationRequest } from '../model/citationRequest';
import type { AttachedStatementReference } from '../model/extractedStatements';

/**
 * Per-citation-request Zustand store for the `CitationRequestReviewSheet`.
 *
 * Owns the expert's local approve/reject toggles while the review
 * sheet is open, plus the snapshot of the message's currently-attached
 * refs from this request and the request's proposed-reference count.
 * Those last two are synced from the message's live references by the
 * `useCitationRequestReviewSheet` hook, so all of the sheet's derived
 * state (`hasUnpublishedChanges`, `publishCount`, `isDoneState`,
 * `publishLabel`, `isPublishDisabled`, `hasCandidates`) can be
 * computed by pure selectors here — both sheet variants render the
 * same machine without duplicating the policy.
 *
 * The store does not perform side effects (no fetch, no analytics, no
 * query invalidation). Orchestration lives in
 * `useCitationRequestReviewSheet`.
 */

type CitationRequestReviewState = {
  isOpen: boolean;
  /** Approved candidate references. Membership toggles via
   * `toggleApproval`; cleared on close. */
  approvedReferenceIds: Set<string>;
  /** Snapshot of which proposed references are currently attached to
   * the parent message. Synced from outside via
   * `syncAttachedFromThisRequest` whenever the message's references
   * changes. Driving `hasUnpublishedChanges`. */
  attachedFromThisRequestIds: Set<string>;
  /** Proposed-reference count of the request the sheet is open
   * against. Synced via `syncProposedReferenceCount`. Drives
   * `hasCandidates`. */
  proposedReferenceCount: number;
  /** True while the publish mutation is in flight. Approve and
   * Cancel CTAs should be disabled while this is true. */
  isPublishing: boolean;
  /** Inline error message rendered when publish throws. Cleared on
   * the next `beginPublish`. */
  errorMessage: string | null;
  /** Count of statements whose local help-request input has non-empty
   * text. Synced from the per-message help-request store by the
   * review hook. Adds to the publish count alongside approved
   * candidates: every non-empty draft becomes a published
   * `statementReferenceHelpRequests` entry on the next Publish. */
  helpRequestDraftCount: number;
  /** True when at least one local help-request draft differs from
   * the persisted `statementReferenceHelpRequests` entry for that
   * statement (insert, update, or remove). Synced from the per-
   * message help-request store by the review hook. Drives
   * `hasUnpublishedChanges` so the Publish CTA fires for help-
   * request-only changes too. */
  helpRequestHasUnpublishedChanges: boolean;
};

type CitationRequestReviewActions = {
  /** Opens the review sheet with the supplied initial approval set
   * and resets `isPublishing` / `errorMessage`. Does not touch the
   * snapshot of attached refs or the proposed-reference count — those
   * stay live via the dedicated sync actions and the hook's sync
   * effects. Callers that open the sheet from outside (e.g.
   * `openCitationRequestReview`) should call the sync actions first
   * so the derived selectors are correct on the very first render. */
  open: (initialApprovedReferenceIds: readonly string[]) => void;
  /** Toggles `isOpen` without touching anything else. Use this when
   * the user dismisses the sheet but the request is still reviewable
   * — the next `open()` will re-seed approvals from the message's
   * attached refs. */
  setOpen: (isOpen: boolean) => void;
  /** Clears all local state back to defaults, including `isOpen:
   * false`. Use this when the request has become unreviewable
   * (failed, cancelled, retried) and the in-progress approval set is
   * no longer meaningful. */
  reset: () => void;
  /** Flip approval for `referenceId`. */
  toggleApproval: (referenceId: string) => void;
  beginPublish: () => void;
  /** Finalise a publish. When `error` is supplied, sets `errorMessage`
   * and leaves approval state intact so the expert can retry. */
  endPublish: (error?: Error) => void;
  /** Refresh the snapshot of attached refs from this request. Called
   * by the hook whenever the parent message's
   * `attachedStatementReferences` changes (e.g. realtime sync). */
  syncAttachedFromThisRequest: (attachedReferenceIds: readonly string[]) => void;
  /** Refresh the proposed-reference count when the request payload
   * changes. */
  syncProposedReferenceCount: (count: number) => void;
  /** Push the latest help-request draft summary into the store. The
   * review hook computes the count and unpublished flag from the
   * per-message help-request store and the persisted entries, and
   * calls this every time the underlying draft state changes. */
  syncHelpRequestSummary: (summary: { draftCount: number; hasUnpublishedChanges: boolean }) => void;
};

export type CitationRequestReviewStore = CitationRequestReviewState & CitationRequestReviewActions;

const INITIAL_STATE: CitationRequestReviewState = {
  isOpen: false,
  approvedReferenceIds: new Set(),
  attachedFromThisRequestIds: new Set(),
  proposedReferenceCount: 0,
  isPublishing: false,
  errorMessage: null,
  helpRequestDraftCount: 0,
  helpRequestHasUnpublishedChanges: false,
};

/* ---------- Selectors --------------------------------------------------- */

export const selectIsOpen = (state: CitationRequestReviewStore): boolean => state.isOpen;

export const selectIsPublishing = (state: CitationRequestReviewStore): boolean =>
  state.isPublishing;

export const selectErrorMessage = (state: CitationRequestReviewStore): string | null =>
  state.errorMessage;

export const selectApprovedReferenceIds = (state: CitationRequestReviewStore): Set<string> =>
  state.approvedReferenceIds;

export const selectPublishCount = (state: CitationRequestReviewStore): number =>
  state.approvedReferenceIds.size + state.helpRequestDraftCount;

export const selectIsApproved = (state: CitationRequestReviewStore, referenceId: string): boolean =>
  state.approvedReferenceIds.has(referenceId);

export const selectHasCandidates = (state: CitationRequestReviewStore): boolean =>
  state.proposedReferenceCount > 0;

export const selectHasUnpublishedChanges = (state: CitationRequestReviewStore): boolean => {
  if (state.helpRequestHasUnpublishedChanges) {
    return true;
  }

  const approved = state.approvedReferenceIds;

  const attached = state.attachedFromThisRequestIds;

  if (approved.size !== attached.size) {
    return true;
  }

  for (const id of approved) {
    if (!attached.has(id)) {
      return true;
    }
  }

  return false;
};

export const selectIsDoneState = (state: CitationRequestReviewStore): boolean =>
  !selectHasUnpublishedChanges(state) && selectPublishCount(state) > 0;

/**
 * Publish is disabled whenever there's nothing to write. The "Done"
 * fast-path was removed — having selections that exactly match the
 * persisted state is functionally identical to having no selections at
 * all: no diff, nothing to publish, button disabled. Cancel in the
 * header handles dismiss for both states.
 */
export const selectIsPublishDisabled = (state: CitationRequestReviewStore): boolean =>
  !selectHasUnpublishedChanges(state);

/* ---------- Factory + registry ----------------------------------------- */

export function createCitationRequestReviewStore(): StoreApi<CitationRequestReviewStore> {
  return createStore<CitationRequestReviewStore>((set) => ({
    ...INITIAL_STATE,

    open: (initialApprovedReferenceIds) =>
      set({
        isOpen: true,
        approvedReferenceIds: new Set(initialApprovedReferenceIds),
        isPublishing: false,
        errorMessage: null,
      }),

    setOpen: (isOpen) => set({ isOpen }),

    reset: () => set({ ...INITIAL_STATE }),

    toggleApproval: (referenceId) =>
      set((state) => {
        const next = new Set(state.approvedReferenceIds);

        if (next.has(referenceId)) {
          next.delete(referenceId);
        } else {
          next.add(referenceId);
        }

        return { approvedReferenceIds: next };
      }),

    beginPublish: () => set({ isPublishing: true, errorMessage: null }),

    endPublish: (error) =>
      set({
        isPublishing: false,
        errorMessage: error ? error.message : null,
      }),

    syncAttachedFromThisRequest: (attachedReferenceIds) =>
      set((state) => {
        const next = new Set(attachedReferenceIds);

        if (
          next.size === state.attachedFromThisRequestIds.size &&
          [...next].every((id) => state.attachedFromThisRequestIds.has(id))
        ) {
          return state;
        }

        return { attachedFromThisRequestIds: next };
      }),

    syncProposedReferenceCount: (count) =>
      set((state) =>
        state.proposedReferenceCount === count ? state : { proposedReferenceCount: count }
      ),

    syncHelpRequestSummary: ({ draftCount, hasUnpublishedChanges }) =>
      set((state) => {
        if (
          state.helpRequestDraftCount === draftCount &&
          state.helpRequestHasUnpublishedChanges === hasUnpublishedChanges
        ) {
          return state;
        }

        return {
          helpRequestDraftCount: draftCount,
          helpRequestHasUnpublishedChanges: hasUnpublishedChanges,
        };
      }),
  }));
}

const storeMap = new Map<string, StoreApi<CitationRequestReviewStore>>();

export function getCitationRequestReviewStore(
  citationRequestId: string
): StoreApi<CitationRequestReviewStore> {
  let store = storeMap.get(citationRequestId);

  if (!store) {
    store = createCitationRequestReviewStore();

    storeMap.set(citationRequestId, store);
  }

  return store;
}

export function deleteCitationRequestReviewStore(citationRequestId: string): void {
  storeMap.delete(citationRequestId);
}

/** Test-only: clear the registry between tests. */
export function __resetCitationRequestReviewStoreRegistry(): void {
  storeMap.clear();
}

/**
 * Seed the per-citation-request review store before the wizard opens
 * onto its review step. Called by `AddReferencesPromptBubble` when
 * the chat-bubble pill is tapped on a request whose status is
 * `awaiting_review` or `published`, immediately before `openTriage()`.
 *
 * Seeds `approvedReferenceIds` AND `attachedFromThisRequestIds` from
 * the message's currently-attached references so the derived
 * selectors (`hasUnpublishedChanges`, `isDoneState`, …) report
 * correctly on the very first render — no flicker through a
 * pre-sync state.
 */
export function openCitationRequestReview(
  citationRequest: CitationRequest,
  currentAttachedReferences: readonly AttachedStatementReference[]
): void {
  const attachedKeys = new Set(
    currentAttachedReferences.map((entry) => `${entry.statementIndex}::${entry.url}`)
  );

  const attachedFromThisRequestIds = citationRequest.proposedReferences
    .filter((reference) => attachedKeys.has(`${reference.statementIndex}::${reference.url}`))
    .map((reference) => reference.id);

  const store = getCitationRequestReviewStore(citationRequest.id);

  store.getState().syncProposedReferenceCount(citationRequest.proposedReferences.length);

  store.getState().syncAttachedFromThisRequest(attachedFromThisRequestIds);

  store.getState().open(attachedFromThisRequestIds);
}
